import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Heart, MessageCircle, Share2, Send, Image as ImageIcon } from "lucide-react";
import { fmtTimeAgo } from "../lib/format";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const TYPES = [
    { key: "", label: "All" },
    { key: "success", label: "Success" },
    { key: "update", label: "Updates" },
    { key: "help", label: "Urgent Help" },
    { key: "general", label: "General" },
];

export default function CommunityFeed() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [type, setType] = useState("");
    const [content, setContent] = useState("");
    const [imgUrl, setImgUrl] = useState("");
    const [postType, setPostType] = useState("general");
    const [busy, setBusy] = useState(false);

    async function load() {
        const { data } = await api.get("/posts", { params: type ? { post_type: type } : {} });
        setPosts(data);
    }
    useEffect(() => { load(); }, [type]);

    async function submit() {
        if (!user) return toast.error("Sign in to post");
        if (!content.trim()) return;
        setBusy(true);
        try {
            await api.post("/posts", { content, image_url: imgUrl || null, post_type: postType });
            setContent(""); setImgUrl(""); toast.success("Posted");
            load();
        } catch { toast.error("Could not post"); }
        setBusy(false);
    }

    async function toggleLike(p) {
        if (!user) return toast.error("Sign in to like");
        try {
            const { data } = await api.post(`/posts/${p.id}/like`);
            setPosts((prev) => prev.map((x) => x.id === p.id ? { ...x, likes: data.likes } : x));
        } catch {}
    }

    return (
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-10" data-testid="community-feed">
            <div className="mb-8">
                <div className="eyebrow mb-3">Community feed</div>
                <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Every wave of hope,<br />in one thread.</h1>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
                {TYPES.map((t) => (
                    <button key={t.label} onClick={() => setType(t.key)}
                            data-testid={`feed-filter-${t.label.toLowerCase()}`}
                            className={`px-4 py-1.5 rounded-full text-sm border ${type === t.key ? "bg-[var(--rl-text)] text-white border-[var(--rl-text)]" : "border-[var(--rl-border)] text-[var(--rl-text-2)] hover:bg-[var(--rl-cream)]"}`}>
                        {t.label}
                    </button>
                ))}
            </div>

            {user && (
                <div className="card-warm rounded-xl p-5 mb-8" data-testid="new-post-card">
                    <Textarea value={content} onChange={(e) => setContent(e.target.value)}
                              placeholder="Share a success, an update, or an urgent call…"
                              className="border-[var(--rl-border)] min-h-[80px]" data-testid="post-content" />
                    <Input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)}
                           placeholder="Image URL (optional)"
                           className="mt-2 border-[var(--rl-border)] text-sm" data-testid="post-image" />
                    <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                        <div className="flex gap-2">
                            {["general", "success", "update", "help"].map((t) => (
                                <button key={t} onClick={() => setPostType(t)}
                                        className={`text-xs uppercase tracking-widest px-3 py-1 rounded-full border ${postType === t ? "bg-[var(--rl-primary)] text-white border-[var(--rl-primary)]" : "border-[var(--rl-border)] text-[var(--rl-text-2)]"}`}>{t}</button>
                            ))}
                        </div>
                        <Button className="btn-primary rounded-full h-10" onClick={submit} disabled={busy || !content.trim()} data-testid="post-submit">
                            <Send className="w-4 h-4 mr-1.5" /> Post
                        </Button>
                    </div>
                </div>
            )}

            {!user && (
                <div className="card-warm rounded-xl p-5 mb-8 text-center">
                    <p className="text-[var(--rl-text-2)]">
                        <Link to="/auth" className="font-semibold text-[var(--rl-primary)]">Sign in</Link> to share your rescue stories and pledges.
                    </p>
                </div>
            )}

            <div className="space-y-5">
                {posts.map((p) => (
                    <article key={p.id} className="card-warm rounded-xl overflow-hidden" data-testid={`post-${p.id}`}>
                        <div className="p-5 pb-3 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[var(--rl-sage)] flex items-center justify-center font-semibold">{p.user_name[0]}</div>
                            <div className="flex-1">
                                <div className="text-sm font-semibold">{p.user_name}</div>
                                <div className="text-xs text-[var(--rl-text-3)]">{fmtTimeAgo(p.created_at)}</div>
                            </div>
                            <Badge className={`text-[10px] uppercase tracking-widest ${
                                p.post_type === "help" ? "bg-[var(--rl-urgent)] text-white hover:bg-[var(--rl-urgent)]" :
                                p.post_type === "success" ? "bg-[var(--rl-success)] text-white hover:bg-[var(--rl-success)]" :
                                "bg-[var(--rl-cream)] text-[var(--rl-text-2)] hover:bg-[var(--rl-cream)]"
                            }`}>{p.post_type}</Badge>
                        </div>
                        <div className="px-5 pb-4">
                            <p className="text-[15px] leading-relaxed">{p.content}</p>
                        </div>
                        {p.image_url && (
                            <div className="border-t border-[var(--rl-border)] bg-[var(--rl-cream)]">
                                <img src={p.image_url} alt="" className="w-full max-h-[500px] object-cover" loading="lazy" />
                            </div>
                        )}
                        <div className="px-5 py-3 border-t border-[var(--rl-border)] flex items-center gap-4 text-sm text-[var(--rl-text-2)]">
                            <button onClick={() => toggleLike(p)} className="flex items-center gap-1.5 hover:text-[var(--rl-urgent)]" data-testid={`like-${p.id}`}>
                                <Heart className="w-4 h-4" /> {p.likes}
                            </button>
                            <span className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> {p.comments_count}</span>
                            <button className="ml-auto flex items-center gap-1.5 hover:text-[var(--rl-primary)]"><Share2 className="w-4 h-4" /> Share</button>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
}
